import React, { useEffect, useMemo, useState } from 'react'
import { Tooltip, useMediaQuery, useTheme, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import i18next from 'i18next'

import CardActionButton from 'components/global/CardActions/CardActionButton'
import CardActionsButtonsGroup from 'components/global/CardActions/CardActionsButtonsGroup'
import TruncatedText from 'components/global/Typography/TruncatedText'
import { useResponsiveTableContext } from 'packages/ui/Table'
import { createExpandedRowComponent } from 'packages/ui/Table'
import Checkbox from 'packages/ui/Checkbox'

import { useCouponsSelected, useLastCouponChecked, Coupon } from '../store'
import { PrintButton, PrintNotebookButton } from '../Card/CardStandard'
import { formatValidity } from '../../../utils'
import { AvailableCoupon, AvailableSoonCoupon, DesactivateCoupon, ExpiredCoupon, UsedCoupon } from '../Card/Widgets'
import usePdf from '../use-pdf'
import { trackEvent } from 'analytics'


interface Context {
  t: (key: string) => string
}

const HeaderCheckbox = (ctx) => {
  const [couponsSelected, setCouponsSelected] = useCouponsSelected()

  return (
    <Checkbox
      checked={false}
      indeterminate={couponsSelected.length > 0}
      onChange={() => {
        if (couponsSelected.length) {
          setCouponsSelected([])
        } else {
          setCouponsSelected(ctx.items)
        }
      }}
    />
  )
}

const BodyCheckbox = ({ coupon, ctx }) => {
  const [couponsSelected, setCouponsSelected] = useCouponsSelected()
  const [lastCouponChecked, setLastCouponChecked] = useLastCouponChecked()
  const isSelected = useMemo(() => couponsSelected.some(selected => selected.id === coupon.id), [couponsSelected, coupon?.id])
  
  const handleMultiSelect = () => {
    const fromIndex = ctx.items.findIndex(item => item.id === lastCouponChecked?.id)
    const toIndex = ctx.items.findIndex(item => item.id === coupon.id)
    const start = Math.min(toIndex, fromIndex)
    const end = Math.max(toIndex, fromIndex)
    const newItems: Coupon[] = []
    for (let i = start; i <= end; i++) {
      newItems.push(ctx.items[i])
    }
    setLastCouponChecked(null)
    setCouponsSelected(prev => {
      const prevSfIds = prev.map(({id}) => id)
      return [...prev, ...newItems.filter(item => !prevSfIds.includes(item?.id))]
    })
  }

  const handleChange = (event) => {
    event.stopPropagation()
    if (event.nativeEvent.shiftKey && lastCouponChecked !== null) {
      handleMultiSelect()
    } else {
      if (isSelected) {
        setCouponsSelected(prev => prev.filter(selected => selected.id !== coupon.id))
      } else {
        setLastCouponChecked(coupon)
        setCouponsSelected(prev => [...prev, coupon]);
      }
    }
  }

  return (
    <Checkbox
      checked={isSelected}
      onChange={handleChange}
    />
  )
}

const Actions: React.FC<{ coupon: Coupon; ctx: Context }> = ({ coupon, ctx }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const responsiveTableContext = useResponsiveTableContext()
  const { findCouponByCorrelationId } = usePdf()
  const [correlationCount, setCorrelationCount] = useState<number>(0)

  useEffect(() => {
    const fetchCorrelationCount = async () => {
      try {
        const { data } = await findCouponByCorrelationId({
          variables: { findCouponByCorrelationIdId: coupon.correlation_id, langue: localStorage.i18nextLng },
          fetchPolicy: 'no-cache'
        })

        const correlation =
          data?.findCouponByCorrelationId.filter((coupon: Coupon) => coupon.available === true || coupon.available_soon > 0) || []

        setCorrelationCount(correlation.length)
      } catch (err) {
        console.error('Error fetching correlation data:', err)
      }
    }

    if (coupon.correlation_id) {
      fetchCorrelationCount()
    }
  }, [coupon, findCouponByCorrelationId])

  return (
    <CardActionsButtonsGroup>
      {!(coupon.expired || coupon.disabled) && (
        <>
          <CardActionButton
            tooltiptitle={ctx.t('coupons.actions.edit.button_tooltip')}
            iconlabel={'edit'}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              e.preventDefault()
              trackEvent('edit_code_button', { code_type: 'standard' })
              navigate(`/codes/standard/${coupon.id}/edit`)
            }}
          />
          <CardActionButton
            tooltiptitle={ctx.t('coupons.actions.send_code.button_tooltip')}
            iconlabel={'comment-dots'}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              e.preventDefault()
              trackEvent('send_code_button', { code_type: 'standard' })
              navigate(`/codes/standard/${coupon.id}/send-sms`)
            }}
          />
        </>
      )}
      {coupon.available_soon > 0 || coupon.valid ? (
        <>
          <PrintButton coupon={coupon} hasBorder={false} />
          {correlationCount > 1 && (
            <PrintNotebookButton coupon={coupon} hasBorder={false} />
          )}
        </>
      ) : null}
      {isMobile && responsiveTableContext && (
        <CardActionButton
          tooltiptitle={responsiveTableContext.isRowExpanded(coupon.id) ? ctx.t('coupons.actions.collapse_details') : ctx.t('coupons.actions.expand_details')}
          iconlabel={responsiveTableContext.isRowExpanded(coupon.id) ? 'chevron-up' : 'chevron-down'}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            e.preventDefault()
            responsiveTableContext.toggleRowExpansion(coupon.id)
          }}
        />
      )}
    </CardActionsButtonsGroup>
  )
}

const Validity: React.FC<{ coupon: Coupon }> = ({ coupon }) => {
  const { t } = useTranslation()
  if (coupon.starts_at && coupon.ends_at) {
    return (
      <>
        {t('coupons.table.validity_col.from') + new Date(coupon.starts_at).toLocaleDateString(i18next.language)}
        <br />
        {t('coupons.table.validity_col.to') + new Date(coupon.ends_at).toLocaleDateString(i18next.language)}
      </>
    )
  }
  const validity = formatValidity(coupon.validity_period, t)
  if (validity.length > 1) {
    return (
      <Tooltip title={validity.join(' ')} placement="bottom" enterDelay={250} enterNextDelay={250} arrow>
        <span style={{ cursor: 'pointer' }}>{validity[0]} +</span>
      </Tooltip>
    )
  }
  return validity[0]
}

export const Status: React.FC<{ coupon: Coupon }> = ({ coupon }) => {
  if (coupon.available_soon > 0) {
    return <AvailableSoonCoupon count={coupon.available_soon} table />
  }
  if (coupon.available && coupon.valid) {
    return <AvailableCoupon table />
  }
  if (coupon.expired) {
    return <ExpiredCoupon table />
  }
  if (coupon.used && coupon.valid) {
    return <UsedCoupon table />
  }
  if (coupon.disabled) {
    return <DesactivateCoupon table />
  }
  return null
}

export const ExpandedRowDetails = createExpandedRowComponent<Coupon>((coupon, t) => [
  {
    key: 'phones',
    label: t('coupons.table.phones_col'),
    value: coupon.phone || '-'
  },
  {
    key: 'description',
    label: t('coupons.table.description_col'),
    value: coupon.description || '-'
  },
  {
    key: 'creation_date',
    label: t('coupons.table.creation_date_col'),
    value: new Date(coupon.created_at).toLocaleDateString(i18next.language)
  },
  {
    key: 'created_by',
    label: t('coupons.table.created_by_col'),
    value: coupon.creator_firstname && coupon.creator_lastname
      ? `${coupon.creator_firstname} ${coupon.creator_lastname}`
      : coupon.created_by || '-'
  },
  {
    key: 'offers',
    label: t('coupons.table.offers_col'),
    value: coupon.offer?.name || '-'
  },
  {
    key: 'connexions',
    label: t('coupons.table.connexions_col'),
    value: coupon.number_of_devices?.toString() || '-'
  },
  {
    key: 'validity',
    label: t('coupons.table.validity_col.header'),
    value: coupon.starts_at && coupon.ends_at 
      ? `${t('coupons.table.validity_col.from')}${new Date(coupon.starts_at).toLocaleDateString(i18next.language)} - ${t('coupons.table.validity_col.to')}${new Date(coupon.ends_at).toLocaleDateString(i18next.language)}`
      : formatValidity(coupon.validity_period, t).join(' ')
  }
])

const COLUMNS_CONFIG = [
  {
    name: 'check',
    header: {
      label: HeaderCheckbox,
    },
    style: {
      width: 24,
      paddingLeft: 8,
    },
    value: (coupon, ctx) => <BodyCheckbox coupon={coupon} ctx={ctx}/>,
    priority: 1
  },
  {
    name: 'availability',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.status_col')
    },
    value: (coupon: Coupon) => <Status coupon={coupon} />,
    priority: 1
  },
  {
    name: 'codes',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.codes_col')
    },
    value: (coupon: Coupon) => coupon.code,
    priority: 1
  },
  {
    name: 'sites',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.sites_col')
    },
    value: (coupon: Coupon) => (
      <TruncatedText 
        text={coupon.site?.name} 
        maxWidth="120px" 
        maxLength={15}
      />
    ),
    priority: 1
  },
  {
    name: 'offers',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.offers_col')
    },
    value: (coupon: Coupon) => (
      <TruncatedText 
        text={coupon.offer?.name} 
        maxWidth="130px" 
        maxLength={16}
      />
    ),
    priority: 2
  },
  {
    name: 'connexions',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.connexions_col')
    },
    value: (coupon: Coupon) => coupon.number_of_devices,
    sortBy: 'number_of_devices',
    sortable: true,
    priority: 2
  },
  {
    name: 'validity',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.validity_col.header')
    },
    value: (coupon: Coupon) => <Validity coupon={coupon} />,
    sortBy: 'validity_period',
    sortable: true,
    priority: 2
  },
  {
    name: 'phones',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.phones_col')
    },
    value: (coupon: Coupon) => coupon.phone,
    priority: 4
  },
  {
    name: 'description',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.description_col')
    },
    value: (coupon: Coupon) => (
      <TruncatedText 
        text={coupon.description} 
        maxWidth="200px" 
        maxLength={30}
      />
    ),
    priority: 3
  },
  {
    name: 'creation_date',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.creation_date_col')
    },
    value: (coupon: Coupon) => new Date(coupon.created_at).toLocaleDateString(i18next.language),
    sortBy: 'created_at',
    sortable: true,
    priority: 3
  },
  {
    name: 'created_by',
    header: {
      label: (ctx: Context) => ctx.t('coupons.table.created_by_col')
    },
    value: (coupon: Coupon) => {
      const createdBy = coupon.creator_firstname && coupon.creator_lastname
        ? `${coupon.creator_firstname} ${coupon.creator_lastname}`
        : coupon.created_by
      
      return (
        <TruncatedText 
          text={createdBy} 
          maxWidth="140px" 
          maxLength={18}
        />
      )
    },
    priority: 4
  },
  {
    name: 'actions',
    header: {
      label: (ctx: Context) => (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          width: '100%'
        }}>
          <Box 
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '200px'
            }}
            title={ctx.t('coupons.table.actions_col')}
          >
            {ctx.t('coupons.table.actions_col')}
          </Box>
        </Box>
      )
    },
    value: (coupon: Coupon, ctx: Context) => (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        width: '100%'
      }}>
        <Actions coupon={coupon} ctx={ctx} />
      </Box>
    ),
    priority: 1,
    style: { textAlign: 'right' }
  }
]

export default COLUMNS_CONFIG
